import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from './AppHeader';
import Footer from './Footer';

// Backend API URL - configurable per environment
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

const GeneratedReportPage = () => {
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalImagesGenerated: 0,
    creditsAvailable: 0,
    creditsUsed: 0,
    creditsPurchased: 0,
    bonusCredits: 0,
    adminRechargeCredits: 0,
    totalBonusCredits: 0,
    totalAmountSpent: 0
  });
  const [walletInfo, setWalletInfo] = useState({ credits: 0 });
  const [downloadingWallet, setDownloadingWallet] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Fetch report data
  useEffect(() => {
    const fetchReport = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(`${BACKEND_URL}/api/report`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          timeout: 10000
        });

        if (response.data.success) {
          setStats(response.data.data.summary);
          
          // Also fetch wallet info for header
          const walletResponse = await axios.get(`${BACKEND_URL}/api/wallet`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (walletResponse.data.success) {
            setWalletInfo(walletResponse.data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching report:', error);
        toast.error('Failed to load report data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated()) {
      fetchReport();
    }
  }, [isAuthenticated]);

  // Handle wallet report download
  const handleDownloadWalletReport = async () => {
    try {
      setDownloadingWallet(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/report/download-wallet`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
        timeout: 30000
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wallet_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Wallet report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading wallet report:', error);
      toast.error('Failed to download wallet report. Please try again.');
    } finally {
      setDownloadingWallet(false);
    }
  };

  // Handle images report download
  const handleDownloadImagesReport = async () => {
    try {
      setDownloadingImages(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/report/download-images`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
        timeout: 30000
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `images_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Images report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading images report:', error);
      toast.error('Failed to download images report. Please try again.');
    } finally {
      setDownloadingImages(false);
    }
  };

  // Handle recharge
  const handleRecharge = () => {
    // This will be handled by the RechargeModal component
    // For now, navigate to main page or show recharge modal
    navigate('/');
  };

  if (loading) {
    return (
      <div className="App">
        <AppHeader
          credits={walletInfo.credits}
          generated={stats.creditsUsed}
          user={user}
          onLogout={logout}
          onRecharge={handleRecharge}
        />
        <main className="container" style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="loading-spinner-large"></div>
          <p>Loading report...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <AppHeader
        credits={walletInfo.credits}
        generated={stats.creditsUsed}
        user={user}
        onLogout={logout}
        onRecharge={handleRecharge}
      />
      <main className="container" style={{ padding: '2rem 1rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div className="report-page">
          {/* Page Header */}
          <div className="report-header">
            <h1 className="report-title">📊 Generated Report</h1>
            <div className="report-download-buttons">
              <button
                className="download-report-btn wallet-btn"
                onClick={handleDownloadWalletReport}
                disabled={downloadingWallet || downloadingImages}
              >
                {downloadingWallet ? (
                  <>
                    <span className="spinner-small"></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    💳 Download Wallet Report
                  </>
                )}
              </button>
              <button
                className="download-report-btn images-btn"
                onClick={handleDownloadImagesReport}
                disabled={downloadingWallet || downloadingImages}
              >
                {downloadingImages ? (
                  <>
                    <span className="spinner-small"></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    🖼️ Download Images Report
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="report-summary-grid">
            <div className="report-card">
              <div className="report-card-icon">🖼️</div>
              <div className="report-card-content">
                <h3 className="report-card-label">Total Images Generated</h3>
                <p className="report-card-value">{stats.totalImagesGenerated}</p>
              </div>
            </div>

            <div className="report-card">
              <div className="report-card-icon">💎</div>
              <div className="report-card-content">
                <h3 className="report-card-label">Credits Available</h3>
                <p className="report-card-value">{stats.creditsAvailable}</p>
              </div>
            </div>

            <div className="report-card">
              <div className="report-card-icon">📊</div>
              <div className="report-card-content">
                <h3 className="report-card-label">Credits Used</h3>
                <p className="report-card-value">{stats.creditsUsed}</p>
              </div>
            </div>

            <div className="report-card">
              <div className="report-card-icon">💰</div>
              <div className="report-card-content">
                <h3 className="report-card-label">Total Amount Spent</h3>
                <p className="report-card-value">₹{parseFloat(stats.totalAmountSpent || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="report-card">
              <div className="report-card-icon">💳</div>
              <div className="report-card-content">
                <h3 className="report-card-label">Credits Purchased</h3>
                <p className="report-card-value">{stats.creditsPurchased || 0}</p>
              </div>
            </div>

            <div className="report-card">
              <div className="report-card-icon">🎁</div>
              <div className="report-card-content">
                <h3 className="report-card-label">Bonus Credits Received</h3>
                <p className="report-card-value">{stats.bonusCredits || 0}</p>
                {stats.adminRechargeCredits > 0 && (
                  <p className="report-card-subtitle" style={{ fontSize: '0.75rem', color: '#718096', marginTop: '0.25rem' }}>
                    Admin Recharge: {stats.adminRechargeCredits}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="report-info-section">
            <h2 className="report-section-title">Report Information</h2>
            <div className="report-info-content">
              <p>
                This report shows your usage statistics including total images generated, 
                credits available and used, and payment history. Use the download buttons 
                above to get detailed CSV reports:
              </p>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li><strong>Wallet Report:</strong> Contains credits summary and all payment transaction details</li>
                <li><strong>Images Report:</strong> Contains all image generation details including category, style, and costs</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GeneratedReportPage;

